import { sortQuotesByDlvdOffer } from "../panels/multiLocationQuotePanel/components/multiLocationQuoteUtils";
import {
  IDestination,
  IGetQuotesResponse,
  IQuoteExtension,
  IQuotesByDestination,
} from "../types/ICompAnalysisQMD";
import ILocation from "../types/ILocation";
import { removeCity } from "./crap";
import NitroService from "./nitro.service";
import { toCamelCase } from "./String";

const generateDisplayName = (
  destination: IDestination,
  destinationDetails?: ILocation,
) => {
  const { city, stateOrProvince, name, isCity } = destination;
  // if isCity is true city will also be defined
  if (isCity && city) {
    return `${toCamelCase(city)}, ${stateOrProvince}`;
  }
  if (!destinationDetails) return undefined;
  const { pinType } = destinationDetails;
  if (pinType === "customer") {
    return removeCity(name, destinationDetails.city || "")
      .split(" ")
      .map(toCamelCase)
      .join(" ");
  }
};

const findAvailableProducts = (details?: ILocation) => {
  if (!details) return [];
  const productsSet = new Set<string>();
  details.occupants.forEach((occupant) => {
    occupant.products.forEach((product) => {
      productsSet.add(product.product);
    });
  });
  return Array.from(productsSet);
};

// TODO: why is the boolean flag there?
const extendQuotes = (
  quotesToExtend: IGetQuotesResponse[],
  destinationDetailsMap: Map<string, ILocation>,
  cityZipMap: Map<string, string>,
  isSurroundingLocation = false,
) => {
  return quotesToExtend.map((entry) => {
    const { destination, quotes } = entry;

    // resolve origin
    const extendedQuotes: IQuoteExtension[] = quotes.map((quote) => {
      return {
        ...quote,
        initialOffer:
          !quote.freight ||
          !quote.postedPrice ||
          typeof quote.postedPrice !== "number"
            ? undefined
            : (quote.postedPrice + quote.freight.totalFreight).toFixed(0),
      };
    });

    // resolve destination
    let destinationDetails: ILocation | undefined;
    const cityZip = cityZipMap.get(destination.id);
    if (!destination.isCity) {
      destinationDetails = destinationDetailsMap.get(destination.id);
      if (!destinationDetails) return null;
    }

    return {
      destination: {
        ...destination,
        details: destinationDetails,
        availableProducts: findAvailableProducts(destinationDetails),
        isSurroundingLocation,
        displayName:
          generateDisplayName(destination, destinationDetails) ||
          destination.name,
        city: destination.isCity
          ? destination.city
          : destinationDetails?.city || "",
        stateOrProvince: destination.isCity
          ? destination.stateOrProvince
          : destinationDetails?.state || "",
        pinType: destination.isCity
          ? "city"
          : destinationDetails?.pinType || "",
        zipCode: destination.isCity ? cityZip : undefined,
      },
      quotes: sortQuotesByDlvdOffer(extendedQuotes),
    } as IQuotesByDestination;
  });
};

// fetch freight zip codes for city-pin destinations (no location details exist for these)
const getCityZipMap = async (
  quotes: IGetQuotesResponse[],
): Promise<Map<string, string>> => {
  const cityZipMap = new Map<string, string>();
  const cityDestinations = quotes.filter((entry) => entry.destination.isCity);

  await Promise.all(
    cityDestinations.map(async ({ destination }) => {
      if (!destination.city || !destination.stateOrProvince) return;

      const cityState = `${destination.city.toUpperCase()}, ${destination.stateOrProvince.toUpperCase()}`;

      try {
        const zipCode = await NitroService.getCityStateFreightZip(cityState);
        if (zipCode) {
          cityZipMap.set(destination.id, zipCode);
        }
      } catch (error) {
        console.error("Failed to fetch city state freight zip", error);
      }
    }),
  );

  return cityZipMap;
};

export const convertCompAnalysisQMD = async (
  quotes: IGetQuotesResponse[],
  isSurroundingLocation = false,
): Promise<IQuotesByDestination[]> => {
  // get destination details for non-city destinations in both primary and surrounding locations
  const nonCityIds = quotes
    .filter((entry) => !entry.destination.isCity)
    .map((entry) => entry.destination.id);

  const destinationDetailsMap = new Map<string, ILocation>();

  const [, cityZipMap] = await Promise.all([
    (async () => {
      if (nonCityIds.length > 0) {
        try {
          const allDestinationDetails =
            await NitroService.getMultiLocationDetails2({
              ids: nonCityIds,
            });

          if (allDestinationDetails) {
            allDestinationDetails.forEach((detail) => {
              if (detail?.id) {
                destinationDetailsMap.set(detail.id, detail);
              }
            });
          }
        } catch (error) {
          console.error("Failed to fetch batch destination details", error);
        }
      }
    })(),
    getCityZipMap(quotes),
  ]);

  const extendedQuotes = extendQuotes(
    quotes,
    destinationDetailsMap,
    cityZipMap,
    isSurroundingLocation,
  );

  return extendedQuotes.filter(
    (quote): quote is IQuotesByDestination => quote !== null,
  );
};
